@use './pds-box.mixins' as *;

pds-box {
  --border-width-default: var(--pine-border-width-thin);
  --color-background-box: inherit;
  --color-border-box: inherit;

  display: block;
}

[class*='pds-box'] {
  background-color: var(--color-background-box);
  box-sizing: border-box;
  display: inline-flex;

  &[min-height] {
    min-height: var(--sizing-min-height-box);
  }

  &[min-width] {
    min-width: var(--sizing-min-width-box);
  }

  // The immediate child of the row will fit the width of the row
  .pds-row > & {
    position: relative;
    width: 100%;

    &.pds-box--auto {
      width: auto;
    }
  }
}

// Spacing mixin
$pine-spacing-tokens: (
  xxs: 4px,
  xs: 8px,
  sm: 16px,
  md: 24px,
  lg: 36px,
  xl: 48px,
  xxl: 64px,
);

// Generate spacing classes
@include generate-spacing-classes(false, $pine-spacing-tokens);
@include generate-spacing-classes('top', $pine-spacing-tokens);
@include generate-spacing-classes('right', $pine-spacing-tokens);
@include generate-spacing-classes('left', $pine-spacing-tokens);
@include generate-spacing-classes('bottom', $pine-spacing-tokens);

// Base offset classes
@include generate-column-offsets(false, 12);

// Responsive offset classes
@media (max-width: 575px) {
  @include generate-column-offsets('xs', 12);
}

@media (min-width: 576px) {
  @include generate-column-offsets('sm', 12);
}

@media (min-width: 768px) {
  @include generate-column-offsets('md', 12);
}

@media (min-width: 992px) {
  @include generate-column-offsets('lg', 12);
}

@media (min-width: 1200px) {
  @include generate-column-offsets('xl', 12);
}


// Base column width classes
@include generate-columns(false, 12);

// Responsive column width classes
@media (max-width: 575px) {
  @include generate-columns('xs', 12);
}

@media (min-width: 576px) {
  @include generate-columns('sm', 12);
}

@media (min-width: 768px) {
  @include generate-columns('md', 12);
}

@media (min-width: 992px) {
  @include generate-columns('lg', 12);
}

@media (min-width: 1200px) {
  @include generate-columns('xl', 12);
}

.pds-box {
  flex-basis: 0;
  flex-grow: 1;
}

.pds-row > .pds-box[class*='pds-box-'] {
  flex-basis: auto;
}

.pds-box--auto {
  flex: 0 0 var(--sizing-min-width-box, fit-content);
  min-width: auto;
  width: auto;
}

.pds-box--fit {
  max-width: 100%;
  min-width: 100%;
  width: 100%;
}

.pds-box--border {
  border-color: var(--color-border-box, var(--pine-color-border));
  border-style: solid;
  border-width: var(--border-width-default);
}

// Display helpers
.pds-box--display-flex {
  display: flex;
}

.pds-box--display-inline-flex {
  display: inline-flex;
}

.pds-box--display-block {
  display: block;
}

.pds-box--display-inline-block {
  display: inline-block;
}

// Flex helpers
.pds-box--flex-none {
  flex: 0 0 auto;
}

.pds-box--flex-grow {
  flex: 1 1 auto
}

.pds-box--flex-shrink {
  flex: 0 0 auto;
}

// Border radius helpers
.pds-border-radius-none {
  border-radius: 0;
}

.pds-border-radius-xs {
  border-radius: var(--pine-dimension-2xs);
}

.pds-border-radius-sm {
  border-radius: var(--pine-dimension-xs);
}

.pds-border-radius-md {
  border-radius: var(--pine-dimension-125);
}

.pds-border-radius-lg {
  border-radius: var(--pine-dimension-sm);
}

.pds-border-radius-circle {
  border-radius: var(--pine-border-radius-full);
}

// Box shadow helpers
.pds-shadow-050 {
  box-shadow: var(--pine-box-shadow-050);
}

.pds-shadow-100 {
  box-shadow: var(--pine-box-shadow-100);
}

.pds-shadow-150 {
  box-shadow: var(--pine-box-shadow-150);
}

.pds-shadow-200 {
  box-shadow: var(--pine-box-shadow-200);
}

.pds-shadow-300 {
  box-shadow: var(--pine-box-shadow-300);
}

.pds-shadow-400 {
  box-shadow: var(--pine-box-shadow-400);
}

.pds-shadow-500 {
  box-shadow: var(--pine-box-shadow-500);
}


.pds-shadow-xs {
  box-shadow: var(--pine-box-shadow-050);
}

.pds-shadow-sm {
  box-shadow: var(--pine-box-shadow-100);
}

.pds-shadow-md {
  box-shadow: var(--pine-box-shadow-150);
}

.pds-shadow-lg {
  box-shadow: var(--pine-box-shadow-200);
}

// Spacing values map - includes semantic sizes and numeric tokens
// Used for gap, margin, and padding helpers
$spacing-values: (
  'none': 0,
  'xxs': var(--pine-dimension-2xs),
  'xs': var(--pine-dimension-xs),
  'sm': var(--pine-dimension-sm),
  'md': var(--pine-dimension-md),
  'lg': var(--pine-dimension-lg),
  'xl': var(--pine-dimension-xl),
  'xxl': var(--pine-dimension-2xl),
  '025': var(--pine-dimension-025),
  '050': var(--pine-dimension-050),
  '100': var(--pine-dimension-100),
  '125': var(--pine-dimension-125),
  '150': var(--pine-dimension-150),
  '200': var(--pine-dimension-200),
  '250': var(--pine-dimension-250),
  '300': var(--pine-dimension-300),
  '350': var(--pine-dimension-350),
  '400': var(--pine-dimension-400),
  '450': var(--pine-dimension-450),
  '500': var(--pine-dimension-500),
  '550': var(--pine-dimension-550),
  '600': var(--pine-dimension-600),
  '650': var(--pine-dimension-650),
  '700': var(--pine-dimension-700),
  '750': var(--pine-dimension-750),
  '800': var(--pine-dimension-800)
);

// Gap helpers
@each $name, $value in $spacing-values {
  .pds-box-gap-#{$name} {
    gap: $value;
  }
}

// Margin helpers
@each $name, $value in $spacing-values {
  .pds-margin-block-start-#{$name} {
    margin-block-start: $value;
  }
}

@each $name, $value in $spacing-values {
  .pds-margin-inline-start-#{$name} {
    margin-inline-start: $value;
  }
}

@each $name, $value in $spacing-values {
  .pds-margin-inline-end-#{$name} {
    margin-inline-end: $value;
  }
}

@each $name, $value in $spacing-values {
  .pds-margin-block-end-#{$name} {
    margin-block-end: $value;
  }
}

// Padding helpers
@each $name, $value in $spacing-values {
  .pds-padding-#{$name} {
    padding: $value;
  }
}

// Padding block start helpers
@each $name, $value in $spacing-values {
  .pds-padding-block-start-#{$name} {
    padding-block-start: $value;
  }
}

// Padding block end helpers
@each $name, $value in $spacing-values {
  .pds-padding-block-end-#{$name} {
    padding-block-end: $value;
  }
}

// Padding inline start helpers
@each $name, $value in $spacing-values {
  .pds-padding-inline-start-#{$name} {
    padding-inline-start: $value;
  }
}

// Padding inline end helpers
@each $name, $value in $spacing-values {
  .pds-padding-inline-end-#{$name} {
    padding-inline-end: $value;
  }
}


// Display helpers
.pds-box-display-block {
  display: block;
}

.pds-box-display-inline-block {
  display: inline-block;
}

.pds-box-display-flex {
  display: flex;
}

.pds-box-display-inline-flex {
  display: inline-flex;
}

// Flex Direction helpers
.pds-box-direction-row {
  flex-direction: row;
}

.pds-box-direction-column {
  flex-direction: column;
}

// Flex Wrap helpers
.pds-box--wrap {
  flex-wrap: wrap;
}

// Maps for flex properties
$justify-content-values: (
  'start': start,
  'center': center,
  'end': end,
  'space-between': space-between,
  'space-around': space-around,
  'space-evenly': space-evenly
);

$align-items-values: (
  'start': start,
  'center': center,
  'end': end,
  'baseline': baseline,
  'stretch': stretch
);

$align-self-values: (
  'start': start,
  'center': center,
  'end': end,
  'baseline': baseline,
  'stretch': stretch
);

$breakpoints: (
  'xs': '(max-width: 575px)',
  'sm': '(min-width: 576px)',
  'md': '(min-width: 768px)',
  'lg': '(min-width: 992px)',
  'xl': '(min-width: 1200px)'
);

// Justify Content helpers
@each $name, $value in $justify-content-values {
  .pds-justify-content-#{$name} {
    justify-content: $value;
  }
}

// Responsive Justify Content helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    @each $name, $value in $justify-content-values {
      .pds-justify-content-#{$breakpoint-name}-#{$name} {
        justify-content: $value;
      }
    }
  }
}

// Align Items helpers
@each $name, $value in $align-items-values {
  .pds-align-items-#{$name} {
    align-items: $value;
  }
}

// Responsive Align Items helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    @each $name, $value in $align-items-values {
      .pds-align-items-#{$breakpoint-name}-#{$name} {
        align-items: $value;
      }
    }
  }
}

// Align Self helpers
@each $name, $value in $align-self-values {
  .pds-align-self-#{$name} {
    align-self: $value;
  }
}

// Responsive Align Self helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    @each $name, $value in $align-self-values {
      .pds-align-self-#{$breakpoint-name}-#{$name} {
        align-self: $value;
      }
    }
  }
}

// Responsive Flex Wrap helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    .pds-box--wrap-#{$breakpoint-name} {
      flex-wrap: wrap;
    }
  }
}

// Responsive Auto helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    .pds-box--auto-#{$breakpoint-name} {
      flex: 0 0 var(--sizing-min-width-box, fit-content);
      min-width: auto;
      width: auto;
    }
  }
}

// Responsive Fit helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    .pds-box--fit-#{$breakpoint-name} {
      max-width: 100%;
      min-width: 100%;
      width: 100%;
    }
  }
}

// Responsive Gap helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    @each $name, $value in $spacing-values {
      .pds-box-gap-#{$breakpoint-name}-#{$name} {
        gap: $value;
      }
    }
  }
}

// Responsive Margin helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    @each $name, $value in $spacing-values {
      .pds-margin-block-start-#{$breakpoint-name}-#{$name} {
        margin-block-start: $value;
      }

      .pds-margin-inline-start-#{$breakpoint-name}-#{$name} {
        margin-inline-start: $value;
      }

      .pds-margin-inline-end-#{$breakpoint-name}-#{$name} {
        margin-inline-end: $value;
      }

      .pds-margin-block-end-#{$breakpoint-name}-#{$name} {
        margin-block-end: $value;
      }
    }
  }
}

// Responsive Padding helpers
@each $breakpoint-name, $breakpoint-value in $breakpoints {
  @media #{$breakpoint-value} {
    @each $name, $value in $spacing-values {
      .pds-padding-#{$breakpoint-name}-#{$name} {
        padding: $value;
      }

      .pds-padding-block-start-#{$breakpoint-name}-#{$name} {
        padding-block-start: $value;
      }

      .pds-padding-block-end-#{$breakpoint-name}-#{$name} {
        padding-block-end: $value;
      }

      .pds-padding-inline-start-#{$breakpoint-name}-#{$name} {
        padding-inline-start: $value;
      }

      .pds-padding-inline-end-#{$breakpoint-name}-#{$name} {
        padding-inline-end: $value;
      }
    }
  }
}
